/**
 * TableViewSelector Component
 *
 * A reusable dropdown button for selecting between Compact, Expanded,
 * and Full table view modes. Optionally includes a "Manage columns" action
 * that opens a dialog for toggling column visibility. The dialog is managed
 * internally — the parent only provides column groups, visible keys, and
 * a save callback.
 *
 * Shows a blue indicator dot on the View button when columns have been
 * customized from their defaults.
 */

import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Eye, Columns3 } from "lucide-react"
import ManageColumnsDialog, { ColumnGroup } from "@/components/shared/ManageColumnsDialog"

/** View mode options for table display density. */
export type ViewMode = "compact" | "expanded" | "full" | "status" | "pr-bulk-review"

// Re-export ColumnGroup for convenience so consumers don't need a separate import
export type { ColumnGroup }

/** Display labels for each view mode. */
const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  compact: "Compact",
  expanded: "Expanded",
  full: "Full",
  status: "Extraction Status",
  "pr-bulk-review": "PR Bulk Review",
}

/** Props for the TableViewSelector component. */
interface TableViewSelectorProps<K extends string> {
  /** The currently selected view mode. */
  value: ViewMode
  /** Callback fired when the user selects a different view mode. */
  onChange: (mode: ViewMode) => void
  /** Which view modes to show. Defaults to all three. */
  options?: ViewMode[]
  /** Column groups to display in the Manage Columns dialog. When provided, the menu item is shown. */
  columnGroups?: ColumnGroup<K>[]
  /** Set of currently visible column keys. Required when columnGroups is provided. */
  visibleKeys?: Set<K>
  /** Set of columns that the user should not be able to toggle off. Optional. */
  fixedColumns?: Set<K>
  /** Callback fired with the new visible keys when the user saves column changes. */
  onSaveColumns?: (keys: Set<K>) => void
}

/**
 * Dropdown button for selecting a table view mode with integrated column management.
 *
 * Renders a "View" button that opens a radio group with the specified options.
 * When `columnGroups`, `visibleKeys`, and `onSaveColumns` are provided, a separator
 * and "Manage columns" item appear below the view mode options. Clicking it opens
 * the ManageColumnsDialog internally.
 *
 * A blue indicator dot appears on the View button icon when `columnsModified` is true.
 *
 * @param props - Component props.
 * @param props.value - The currently active view mode.
 * @param props.onChange - Callback when the view mode changes.
 * @param props.options - Which modes to display (defaults to all).
 * @param props.columnGroups - Column groups for the dialog.
 * @param props.visibleKeys - Currently visible column keys.
 * @param props.fixedColumns - Columns that can't be toggled off.
 * @param props.onSaveColumns - Callback when column visibility is saved.
 */
export default function TableViewSelector<K extends string>({
  value,
  onChange,
  options = ["compact", "expanded", "full"],
  columnGroups,
  visibleKeys,
  fixedColumns,
  onSaveColumns,
}: TableViewSelectorProps<K>): React.JSX.Element {
  // Internal dialog open state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)

  /** Whether the Manage columns feature is enabled (all required props provided). */
  const hasColumnManagement = columnGroups !== undefined && visibleKeys !== undefined && onSaveColumns !== undefined

  // Serialize columnGroups keys to detect actual content changes (not reference changes)
  const columnGroupsKey = columnGroups ? columnGroups.map((g) => g.keys.join("|")).join(",") : ""

  // Track the previous columnGroups key to detect real changes
  const prevColumnGroupsKey = useRef<string>(columnGroupsKey)

  // Reset visible columns to all when columnGroups actually changes (i.e. view mode changed)
  useEffect(() => {
    if (prevColumnGroupsKey.current === columnGroupsKey) {
      return
    }
    prevColumnGroupsKey.current = columnGroupsKey
    if (hasColumnManagement && columnGroups && onSaveColumns) {
      const allKeys = new Set(columnGroups.flatMap((g) => g.keys))
      onSaveColumns(allKeys)
    }
  }, [columnGroupsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Whether columns have been modified from their defaults.
   * Compares visibleKeys against all keys present in the current columnGroups.
   * A column is considered "modified" if any key from the groups is missing from visibleKeys.
   */
  const columnsModified = (() => {
    if (!hasColumnManagement || !columnGroups || !visibleKeys) return false
    const allGroupKeys = columnGroups.flatMap((g) => g.keys)
    return !allGroupKeys.every((k) => visibleKeys.has(k))
  })()

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <span className="relative">
              <Eye data-icon="inline-start" className="size-4 mr-1.5" />
              {columnsModified && (
                <span className="absolute -top-1 -right-1 size-2 rounded-full bg-primary" />
              )}
            </span>
            View
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-auto">
          <DropdownMenuRadioGroup value={value} onValueChange={v => onChange(v as ViewMode)}>
            {options.map(mode => (
              <DropdownMenuRadioItem key={mode} value={mode}>
                {VIEW_MODE_LABELS[mode]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {hasColumnManagement && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDialogOpen(true)}>
                <span className="relative">
                  <Columns3 />
                  {columnsModified && (
                    <span className="absolute -top-1 -right-1 size-2 rounded-full bg-primary" />
                  )}
                </span>
                Manage columns
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {hasColumnManagement && (
        <ManageColumnsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          columnGroups={columnGroups}
          visibleKeys={visibleKeys}
          fixedColumns={fixedColumns}
          onSave={onSaveColumns}
        />
      )}
    </>
  )
}
